import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, hasPerm, initials } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Save, Users, Phone, MapPin, Briefcase, Wallet, Eye, UserPlus, MessageCircle, KeyRound, Send, Copy } from "lucide-react";
import { StudentForm } from "@/pages/Students";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const EMPTY = { guardian_name: "", phone: "", alt_phone: "", address: "", profession: "", income: "", whatsapp: "", username: "", password: "" };

export default function ParentDetail() {
  const { key } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canEdit = hasPerm(user, "edit_student");
  const [group, setGroup] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState([]);
  const [addWardOpen, setAddWardOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [loginResult, setLoginResult] = useState(null);

  const sendLogin = async (channel) => {
    if (!group?.login_username) { toast.error("Set a username & password, then Save first."); return; }
    setSending(true);
    setLoginResult(null);
    try {
      const r = await api.put("/parents/detail", { ward_ids: group.wards.map((w) => w.id), ...form });
      if (r.data?.new_key && r.data.new_key !== key) { /* key change handled on next load */ }
      const res = await api.post("/parents/send-login", { ward_ids: group.wards.map((w) => w.id), channel, password: form.password });
      setForm((p) => ({ ...p, password: "" }));
      if (res.data?.sent) {
        toast.success(`Login sent to ${res.data.to} via ${channel === "whatsapp" ? "WhatsApp" : "SMS"}`);
        setLoginResult({ ...res.data, channel });
      } else {
        toast.warning(res.data?.note || "Could not send — share credentials manually.");
        setLoginResult({ ...res.data, channel });
      }
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const copyCreds = () => {
    if (!loginResult) return;
    navigator.clipboard?.writeText(loginResult.message_preview || `Username: ${loginResult.username}, Password: ${loginResult.password}`);
    toast.success("Copied to clipboard");
  };

  const load = () => {
    setLoading(true);
    api.get("/parents/detail", { params: { key } })
      .then((r) => {
        setGroup(r.data);
        setForm({
          guardian_name: r.data.guardian_name || "", phone: r.data.phone || "", alt_phone: r.data.alt_phone || "",
          address: r.data.address || "", profession: r.data.profession || "", income: r.data.income || "",
          whatsapp: r.data.whatsapp || "", username: r.data.login_username || "", password: "",
        });
      })
      .catch(() => toast.error("Could not load family"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [key]);
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);

  const save = async () => {
    if (!form.guardian_name.trim()) { toast.error("Guardian name is required"); return; }
    setSaving(true);
    try {
      const r = await api.put("/parents/detail", { ward_ids: group.wards.map((w) => w.id), ...form });
      toast.success("Parent details updated across all wards");
      const nk = r.data?.new_key;
      setForm((p) => ({ ...p, password: "", username: r.data?.login_username || p.username }));
      if (nk && nk !== key) nav(`/parents/${encodeURIComponent(nk)}`, { replace: true });
      else load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div>;
  if (!group) return null;

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  return (
    <div className="space-y-5 max-w-4xl">
      <button onClick={() => nav("/parents")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600" data-testid="parent-back-btn"><ArrowLeft size={16} />Back to Parent / Sibling</button>

      <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-5" data-testid="parent-detail-header">
        <div className="w-16 h-16 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center text-2xl font-heading font-bold shrink-0">{initials(group.guardian_name || "P")}</div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl font-extrabold text-slate-900 truncate">{group.guardian_name || "Unknown Guardian"}</h1>
          <p className="text-slate-500 flex items-center gap-2"><Users size={15} />{group.ward_count} ward{group.ward_count > 1 ? "s" : ""}</p>
        </div>
        <div className="text-right shrink-0">
          <div className={`text-xl font-heading font-bold tabular ${group.total_due > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(group.total_due)}</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Total Due</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="parent-detail-form">
        <h2 className="font-semibold text-slate-800">Parent / Guardian Details</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-600">Guardian Name</Label>
            <Input value={form.guardian_name} onChange={set("guardian_name")} placeholder="Father / Guardian name" disabled={!canEdit} className="mt-1" data-testid="parent-field-guardian_name" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5 text-slate-600"><Phone size={13} />Phone</Label>
            <Input value={form.phone} onChange={set("phone")} placeholder="Primary phone" disabled={!canEdit} className="mt-1" data-testid="parent-field-phone" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5 text-slate-600"><Phone size={13} />Alternate Phone</Label>
            <Input value={form.alt_phone} onChange={set("alt_phone")} placeholder="Alternate phone" disabled={!canEdit} className="mt-1" data-testid="parent-field-alt_phone" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5 text-slate-600"><MessageCircle size={13} />WhatsApp No.</Label>
            <Input value={form.whatsapp} onChange={set("whatsapp")} placeholder="WhatsApp number" disabled={!canEdit} className="mt-1" data-testid="parent-field-whatsapp" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5 text-slate-600"><Briefcase size={13} />Occupation / Profession</Label>
            <Input value={form.profession} onChange={set("profession")} placeholder="e.g. Farmer, Business" disabled={!canEdit} className="mt-1" data-testid="parent-field-profession" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5 text-slate-600"><Wallet size={13} />Annual Income</Label>
            <Input value={form.income} onChange={set("income")} placeholder="e.g. 3,00,000" disabled={!canEdit} className="mt-1" data-testid="parent-field-income" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5 text-slate-600"><MapPin size={13} />Address</Label>
            <Input value={form.address} onChange={set("address")} placeholder="Home address" disabled={!canEdit} className="mt-1" data-testid="parent-field-address" />
          </div>
        </div>
        <p className="text-xs text-slate-400">Changes apply to all {group.ward_count} ward{group.ward_count > 1 ? "s" : ""} linked to this guardian.</p>

        <div className="border-t border-slate-100 pt-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1.5"><KeyRound size={14} className="text-indigo-600" />Parent Login</h3>
          <p className="text-xs text-slate-400 mb-3">Credentials the parent uses to log into the portal and view their wards' fees, homework and more.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-600">Username</Label>
              <Input value={form.username} onChange={set("username")} placeholder="e.g. ajayvikram" disabled={!canEdit} className="mt-1" data-testid="parent-field-username" />
            </div>
            <div>
              <Label className="text-slate-600">Password</Label>
              <Input type="password" value={form.password} onChange={set("password")} placeholder={group.login_username ? "Leave blank to keep current" : "Set a password"} disabled={!canEdit} className="mt-1" data-testid="parent-field-password" />
            </div>
          </div>
          {group.login_username && <p className="text-xs text-emerald-600 mt-2" data-testid="parent-login-active">Active login: <b>{group.login_username}</b></p>}
          {canEdit && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={sending || !group.login_username} className="gap-1.5" data-testid="parent-send-login-btn">{sending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}Send Login</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-44">
                  <DropdownMenuItem onClick={() => sendLogin("sms")} data-testid="send-login-sms"><MessageCircle size={14} className="mr-2 text-indigo-600" />Via SMS</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => sendLogin("whatsapp")} data-testid="send-login-whatsapp"><MessageCircle size={14} className="mr-2 text-emerald-600" />Via WhatsApp</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <span className="text-xs text-slate-400">Sends username &amp; password to the guardian's {`{phone / WhatsApp}`}.</span>
            </div>
          )}
          {loginResult && !loginResult.sent && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="parent-login-manual">
              <div className="font-medium text-amber-800">{loginResult.note}</div>
              <div className="mt-2 text-slate-700"><b>Username:</b> {loginResult.username} &nbsp; <b>Password:</b> {loginResult.password}</div>
              {loginResult.message_preview && <div className="mt-1 text-xs text-slate-500 italic">"{loginResult.message_preview}"</div>}
              <Button size="sm" variant="outline" onClick={copyCreds} className="mt-2 gap-1.5" data-testid="parent-copy-creds"><Copy size={13} />Copy message</Button>
            </div>
          )}
          {loginResult && loginResult.sent && (
            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="parent-login-sent">
              Sent to {loginResult.to} via {loginResult.channel === "whatsapp" ? "WhatsApp" : "SMS"}.
            </div>
          )}
        </div>
        {canEdit && (
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="parent-save-btn">{saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}Save Changes</Button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="parent-wards">
        <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between">
          <span className="font-semibold text-slate-800">Wards</span>
          {canEdit && <Button size="sm" onClick={() => setAddWardOpen(true)} className="gap-1.5" data-testid="parent-add-ward-btn"><UserPlus size={15} />Add Ward</Button>}
        </div>
        <div className="divide-y divide-slate-100">
          {group.wards.map((w) => (
            <div key={w.id} className="flex items-center gap-3 px-6 py-3" data-testid={`parent-ward-${w.id}`}>
              <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 overflow-hidden flex items-center justify-center text-xs font-semibold shrink-0">{w.photo ? <img src={w.photo} alt={w.name} className="w-full h-full object-cover" /> : initials(w.name)}</div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-800 truncate">{w.name}</div>
                <div className="text-xs text-slate-400">{w.admission_number} · {w.class_name}{w.section ? ` ${w.section}` : ""}</div>
              </div>
              <div className="text-right shrink-0">
                <div className={`font-semibold tabular ${w.fees_summary.due > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(w.fees_summary.due)}</div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Due</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => nav(`/students/${w.id}`)} className="gap-1.5 shrink-0" data-testid={`parent-ward-view-${w.id}`}><Eye size={14} />View</Button>
            </div>
          ))}
        </div>
      </div>

      <StudentForm
        open={addWardOpen}
        onOpenChange={setAddWardOpen}
        classes={classes}
        edit={null}
        initial={{ father_name: form.guardian_name, phone: form.phone, alt_phone: form.alt_phone, address: form.address, father_occupation: form.profession, father_income: form.income }}
        addTitle="Add Ward — New Student"
        onSaved={load}
      />
    </div>
  );
}
